import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react"
import dayjs, { Dayjs } from "dayjs"
import { Spin } from "antd"
import { DragEndEvent } from "@dnd-kit/core"
import { ScheduleState, buildUnifiedEvents, buildEventDisplayInfo, mapDateRangeViewEventToScheduledEvent, UnifiedEventMetadata } from "../scheduleModel"
import { filterConsultations, filterEvents, filterTasks } from '../scheduleFilter'
import { useDateRangeViewEventsQuery, useUpdateScheduledEventMutation, useUpsertTaskEventMutation, useConsultationsByDateRangeQuery, useUpdateConsultationTimeMutation, useTasksByDateRangeQuery } from "../scheduleQueries"
import { DateRangeViewEvent, ScheduledEvent, ConsultationCalendarEvent, SchedulingJob, TaskCalendarEvent } from "../scheduleApi"
import MonthViewWeekRow from "./MonthViewWeekRow"
import { ResizePreview } from "./MonthViewSpanningEvent"
import { isSpanningEvent, isSpanningTask, calculateWeekSpanningLayouts, SpanningEventSegment, buildResizeSpanningEventRequest, getSpanningEventSegments } from "./spanningEventHelpers"
import { buildMoveEventRequest } from "./monthViewModel"
import { findCrewMemberByEmail } from "../crewModel"
import JobDrawer from "../JobDrawer/JobDrawer"
import EditConsultationModal from "../EditConsultation/EditConsultationModal"
import TaskEventModal from "../EditTask/TaskEventModal"
import JobPickerModal from "../CreateEvent/JobPickerModal"
import CreateEventFromJobModal from "../CreateEvent/CreateEventFromJobModal"
import ScheduleEventTypePickerModal from "../CreateEvent/ScheduleEventTypePickerModal"
import { useSchedule } from "../ScheduleContext"

export type Props = {
  state: ScheduleState
  onDateChange: (date: Dayjs) => void
  onOpenOptimize: (date: Dayjs) => void
}

export type ScheduleMonthViewHandle = {
  handleDragEnd: (event: DragEndEvent) => void
}

const DAY_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']

const ScheduleMonthView = forwardRef<ScheduleMonthViewHandle, Props>(({ state, onDateChange, onOpenOptimize }, ref) => {
  const { start: calendarStart, end: calendarEnd } = state.visibleDateRange
  const currentMonth = state.selectedDate.month()
  const totalDays = calendarEnd.diff(calendarStart, 'day') + 1
  const totalWeeks = Math.ceil(totalDays / 7)
  const { crew } = useSchedule()
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [resizePreview, setResizePreview] = useState<ResizePreview>(null)
  const [selectedConsultation, setSelectedConsultation] = useState<ConsultationCalendarEvent | null>(null)
  const [consultationModalOpen, setConsultationModalOpen] = useState(false)
  const [eventTypePickerOpen, setEventTypePickerOpen] = useState(false)
  const [eventTypePickerDay, setEventTypePickerDay] = useState<Dayjs | null>(null)
  const [jobPickerOpen, setJobPickerOpen] = useState(false)
  const [jobPickerDay, setJobPickerDay] = useState<Dayjs | null>(null)
  const [createEventOpen, setCreateEventOpen] = useState(false)
  const [createEventJob, setCreateEventJob] = useState<SchedulingJob | null>(null)
  const [createEventDay, setCreateEventDay] = useState<Dayjs | null>(null)
  const [selectedTask, setSelectedTask] = useState<TaskCalendarEvent | null>(null)
  const [taskModalOpen, setTaskModalOpen] = useState(false)
  const [taskModalDay, setTaskModalDay] = useState<Dayjs | null>(null)
  const updateEventMutation = useUpdateScheduledEventMutation()
  const updateConsultationMutation = useUpdateConsultationTimeMutation()
  const upsertTaskMutation = useUpsertTaskEventMutation()

  const allDays: Dayjs[] = useMemo(() =>
    Array.from({ length: totalDays }, (_, i) => calendarStart.add(i, 'day')),
    [totalDays, calendarStart.format('YYYY-MM-DD')]
  )

  const { data, isLoading } = useDateRangeViewEventsQuery(
    calendarStart.format('YYYY-MM-DD'),
    calendarEnd.format('YYYY-MM-DD')
  )

  const { data: consultationsData, isLoading: consultationsLoading } = useConsultationsByDateRangeQuery(
    calendarStart.format('YYYY-MM-DD'),
    calendarEnd.format('YYYY-MM-DD')
  )

  const { data: tasksData, isLoading: tasksLoading } = useTasksByDateRangeQuery(
    calendarStart.format('YYYY-MM-DD'),
    calendarEnd.format('YYYY-MM-DD')
  )

  const unifiedResult = useMemo(() => {
    if (!data) {
      return null
    }
    return buildUnifiedEvents(data)
  }, [data])

  const { events, calendarEventVisits, visits } = unifiedResult ?? { events: [], calendarEventVisits: [], visits: [] }
  const { visitWorkareaStatuses, timesheets } = data ?? { visitWorkareaStatuses: [], timesheets: [] }
  const eventMetadata: Map<number, UnifiedEventMetadata> = unifiedResult?.eventMetadata ?? new Map()

  const consultations = useMemo(() => {
    return filterConsultations(consultationsData?.consultations ?? [], state.filters, crew ?? [])
  }, [consultationsData, state.filters, crew])

  const weeks: Dayjs[][] = useMemo(() => {
    const result: Dayjs[][] = []
    for (let i = 0; i < totalWeeks; i++) {
      result.push(allDays.slice(i * 7, (i + 1) * 7))
    }
    return result
  }, [allDays, totalWeeks])

  const filteredEvents = useMemo(() => {
    return filterEvents(
      events,
      state.filters,
      visits,
      visitWorkareaStatuses,
      timesheets,
      crew ?? [],
      calendarEventVisits,
      state.visibleDateRange,
      eventMetadata
    )
  }, [events, state.filters, visits, visitWorkareaStatuses, timesheets, crew, calendarEventVisits, state.visibleDateRange, eventMetadata])

  const { spanningEvents, singleDayEvents } = useMemo(() => {
    const spanning: DateRangeViewEvent[] = []
    const singleDay: DateRangeViewEvent[] = []

    for (const event of filteredEvents) {
      if (isSpanningEvent(event)) {
        spanning.push(event)
      } else {
        singleDay.push(event)
      }
    }

    return { spanningEvents: spanning, singleDayEvents: singleDay }
  }, [filteredEvents])

  const { spanningTasks, singleDayTasks } = useMemo(() => {
    const allTasks = filterTasks(tasksData?.tasks ?? [], state.filters, crew ?? [])
    const spanning: TaskCalendarEvent[] = []
    const singleDay: TaskCalendarEvent[] = []

    for (const task of allTasks) {
      if (isSpanningTask(task)) {
        spanning.push(task)
      } else {
        singleDay.push(task)
      }
    }

    return { spanningTasks: spanning, singleDayTasks: singleDay }
  }, [tasksData, state.filters, crew])

  const weekSpanningLayouts = useMemo(() => {
    const weekStarts = weeks.map((week) => week[0])
    return calculateWeekSpanningLayouts(spanningEvents, weekStarts, spanningTasks)
  }, [spanningEvents, weeks, spanningTasks])

  const singleDayEventsByDay: Map<string, DateRangeViewEvent[]> = useMemo(() => {
    const map = new Map<string, DateRangeViewEvent[]>()

    allDays.forEach((day) => {
      const dateKey = day.format('YYYY-MM-DD')
      const dayEvents = singleDayEvents.filter((event) => dayjs(event.start).format('YYYY-MM-DD') === dateKey)
      map.set(dateKey, dayEvents)
    })

    return map
  }, [allDays, singleDayEvents])

  const tasksByDay: Map<string, TaskCalendarEvent[]> = useMemo(() => {
    const map = new Map<string, TaskCalendarEvent[]>()

    allDays.forEach((day) => {
      const dateKey = day.format('YYYY-MM-DD')
      const dayTasks = singleDayTasks.filter((event) => dayjs(event.start).format('YYYY-MM-DD') === dateKey)
      map.set(dateKey, dayTasks)
    })

    return map
  }, [allDays, singleDayTasks])

  const eventDisplayInfo = useMemo(() => {
    return buildEventDisplayInfo(filteredEvents, calendarEventVisits, visits, visitWorkareaStatuses, timesheets, crew ?? [], state.visibleDateRange, eventMetadata)
  }, [filteredEvents, calendarEventVisits, visits, visitWorkareaStatuses, timesheets, crew, state.visibleDateRange, eventMetadata])

  const handleResizePreview = useCallback((preview: ResizePreview) => {
    setResizePreview(preview)
  }, [])

  const previewSegmentsByWeek: Map<number, SpanningEventSegment[]> = useMemo(() => {
    const map = new Map<number, SpanningEventSegment[]>()
    if (!resizePreview) {
      return map
    }

    const originalEvent = events.find(e => e.id === resizePreview.eventId)
    if (!originalEvent) {
      return map
    }

    const modifiedEvent = {
      ...originalEvent,
      end: resizePreview.previewEndDate.toISOString(),
    }

    const weekStarts = weeks.map((week) => week[0])
    const segments = getSpanningEventSegments(modifiedEvent, weekStarts)

    for (const segment of segments) {
      if (segment.weekIndex > resizePreview.weekIndex) {
        const weekSegments = map.get(segment.weekIndex) || []
        weekSegments.push({
          ...segment,
          rowIndex: resizePreview.rowIndex,
        })
        map.set(segment.weekIndex, weekSegments)
      }
    }

    return map
  }, [resizePreview, events, weeks])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    const activeId = String(active.id)

    if (activeId.startsWith('job-')) {
      return
    }

    if (active.data.current?.isResize) {
      return
    }

    if (over && active.data.current?.isConsultation && active.data.current?.consultation) {
      const consultation = active.data.current.consultation as ConsultationCalendarEvent
      const targetDate = over.data.current?.date as Dayjs | undefined

      if (targetDate) {
        const consultationStart = dayjs(consultation.start)
        const consultationEnd = dayjs(consultation.end)
        const durationMinutes = consultationEnd.diff(consultationStart, 'minute')

        const newStart = targetDate.hour(consultationStart.hour()).minute(consultationStart.minute()).second(0)
        const newEnd = newStart.add(durationMinutes, 'minute')

        updateConsultationMutation.mutate({
          id: consultation.id,
          startUtc: newStart.toISOString(),
          endUtc: newEnd.toISOString(),
          start: newStart.format('YYYY-MM-DDTHH:mm:ss'),
          end: newEnd.format('YYYY-MM-DDTHH:mm:ss'),
          startDate: calendarStart.format('YYYY-MM-DD'),
          endDate: calendarEnd.format('YYYY-MM-DD'),
        })
      }
      return
    }

    if (over && active.data.current?.isTask && active.data.current?.event) {
      const droppedEvent = active.data.current.event as TaskCalendarEvent
      const targetDate = over.data.current?.date as Dayjs | undefined

      if (targetDate) {
        const originalStart = dayjs(droppedEvent.start)
        const originalEnd = dayjs(droppedEvent.end)
        const durationMinutes = originalEnd.diff(originalStart, 'minutes')
        const newStart = targetDate.hour(originalStart.hour()).minute(originalStart.minute()).second(0)
        const newEnd = newStart.add(durationMinutes, 'minute')

        upsertTaskMutation.mutate({
          eventId: droppedEvent.id,
          title: droppedEvent.title,
          notes: droppedEvent.notes,
          start: newStart.toISOString(),
          end: newEnd.toISOString(),
          dayStartTime: null,
          estimatedDurationInMinutes: durationMinutes,
          crewLeadId: findCrewMemberByEmail(crew, droppedEvent.crewForemanEmail)?.id ?? null,
          crewMemberIds: droppedEvent.crewMemberIds || '',
          allDay: droppedEvent.allDay,
          sendCrewLeadNotifications: droppedEvent.sendCrewLeadNotifications,
          completedAt: droppedEvent.completedAt,
          frozen: droppedEvent.frozen,
        })
      }
      return
    }

    if (over && active.data.current?.event) {
      const droppedEvent = active.data.current.event as ScheduledEvent
      const targetDate = over.data.current?.date as Dayjs | undefined

      if (activeId.startsWith('spanning-event-')) {
        if (targetDate) {
          const segment = active.data.current.segment as SpanningEventSegment
          const eventStart = dayjs(droppedEvent.start)
          const weekStart = weeks[segment.weekIndex]?.[0]
          if (weekStart) {
            const segmentStartDate = weekStart.add(segment.startDayIndex, 'day')
            const offsetDays = eventStart.startOf('day').diff(segmentStartDate.startOf('day'), 'day')
            const newStartDate = targetDate.add(offsetDays, 'day')

            const request = buildMoveEventRequest({
              event: droppedEvent,
              targetDate: newStartDate,
              crew: crew ?? [],
            })

            if (request) {
              updateEventMutation.mutate(request)
            }
          }
        }
      } else {
        if (targetDate) {
          const request = buildMoveEventRequest({
            event: droppedEvent,
            targetDate,
            crew: crew ?? [],
          })

          if (request) {
            updateEventMutation.mutate(request)
          }
        }
      }
    }
  }

  const handleSpanningEventResize = (event: DateRangeViewEvent, newEndDate: Dayjs) => {
    const request = buildResizeSpanningEventRequest({
      event,
      newEndDate,
      crew: crew ?? [],
    })

    if (request) {
      updateEventMutation.mutate(request)
    }
  }

  const handleSpanningTaskResize = (task: TaskCalendarEvent, newEndDate: Dayjs) => {
    const originalStart = dayjs(task.start)
    const originalEnd = dayjs(task.end)
    const newEnd = newEndDate
      .hour(originalEnd.hour())
      .minute(originalEnd.minute())
      .second(originalEnd.second())
    const durationMinutes = newEnd.diff(originalStart, 'minutes')

    upsertTaskMutation.mutate({
      eventId: task.id,
      title: task.title,
      notes: task.notes,
      start: originalStart.toISOString(),
      end: newEnd.toISOString(),
      dayStartTime: null,
      estimatedDurationInMinutes: durationMinutes,
      crewLeadId: findCrewMemberByEmail(crew, task.crewForemanEmail)?.id ?? null,
      crewMemberIds: task.crewMemberIds || '',
      allDay: task.allDay,
      sendCrewLeadNotifications: task.sendCrewLeadNotifications,
      completedAt: task.completedAt,
      frozen: task.frozen,
    })
  }

  useImperativeHandle(ref, () => ({
    handleDragEnd
  }))

  const handleConsultationClick = (consultation: ConsultationCalendarEvent) => {
    setSelectedConsultation(consultation)
    setConsultationModalOpen(true)
  }

  const handleTaskClick = (event: TaskCalendarEvent) => {
    setSelectedTask(event)
    setTaskModalOpen(true)
  }

  const handleDayScheduleClick = (date: Dayjs) => {
    setEventTypePickerDay(date)
    setEventTypePickerOpen(true)
  }

  const handleEventTypePickerScheduleJob = () => {
    setEventTypePickerOpen(false)
    setJobPickerDay(eventTypePickerDay)
    setJobPickerOpen(true)
  }

  const handleEventTypePickerAddTask = () => {
    setEventTypePickerOpen(false)
    setTaskModalDay(eventTypePickerDay)
    setTaskModalOpen(true)
  }

  const handleMonthJobSelected = (job: SchedulingJob) => {
    setJobPickerOpen(false)
    setCreateEventJob(job)
    setCreateEventDay(jobPickerDay)
    setCreateEventOpen(true)
  }

  const consultationsByDay: Map<string, ConsultationCalendarEvent[]> = useMemo(() => {
    const map = new Map<string, ConsultationCalendarEvent[]>()

    allDays.forEach((day) => {
      const dateKey = day.format('YYYY-MM-DD')
      const dayConsultations = consultations.filter((consultation) => dayjs(consultation.start).format('YYYY-MM-DD') === dateKey)
      map.set(dateKey, dayConsultations)
    })

    return map
  }, [allDays, consultations])

  if (isLoading || consultationsLoading || tasksLoading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '66vh',
          gap: 8,
        }}
      >
        <Spin size="large" />
        <div>Loading...</div>
      </div>
    )
  }

  const selectedEvent = selectedEventId !== null
    ? events.find(e => e.id === selectedEventId) ?? null
    : null
  const selectedEventData = selectedEvent ? {
    event: mapDateRangeViewEventToScheduledEvent(selectedEvent),
    calendarEventVisits,
    visits,
    visitWorkareaStatuses,
    timesheets,
  } : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 80 }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
          borderLeft: '1px solid #e8e8e8',
          borderTop: '1px solid #e8e8e8',
        }}
      >
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #e8e8e8',
            backgroundColor: '#fafbfc',
            flexShrink: 0,
          }}
        >
          {DAY_NAMES.map((dayName) => (
            <div
              key={dayName}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '12px 0',
                fontSize: 12,
                fontWeight: 500,
                color: '#666',
                borderRight: '1px solid #e8e8e8',
              }}
            >
              {dayName}
            </div>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {weeks.map((week, weekIndex) => (
            <MonthViewWeekRow
              key={weekIndex}
              week={week}
              weekIndex={weekIndex}
              currentMonth={currentMonth}
              selectedDate={state.selectedDate}
              eventsByDay={singleDayEventsByDay}
              consultationsByDay={consultationsByDay}
              tasksByDay={tasksByDay}
              eventDisplayInfo={eventDisplayInfo}
              spanningLayout={weekSpanningLayouts[weekIndex]}
              previewSegments={previewSegmentsByWeek.get(weekIndex)}
              onDateChange={onDateChange}
              onEventClick={(event) => {
                setSelectedEventId(event.id)
                setDrawerOpen(true)
              }}
              onConsultationClick={handleConsultationClick}
              onTaskClick={handleTaskClick}
              onSpanningEventClick={(eventId) => {
                setSelectedEventId(eventId)
                setDrawerOpen(true)
              }}
              onSpanningEventResize={handleSpanningEventResize}
              onSpanningTaskResize={handleSpanningTaskResize}
              onResizePreview={handleResizePreview}
              onDayScheduleClick={handleDayScheduleClick}
              onOpenOptimize={onOpenOptimize}
            />
          ))}
        </div>
      </div>

      <JobDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false)
          setSelectedEventId(null)
        }}
        eventData={selectedEventData}
        date={state.selectedDate}
        dateRange={state.visibleDateRange}
        readOnly={selectedEventId !== null && (eventDisplayInfo.get(selectedEventId)?.isDiscreteVisit ?? false)}
      />

      <EditConsultationModal
        open={consultationModalOpen}
        onClose={() => {
          setConsultationModalOpen(false)
          setSelectedConsultation(null)
        }}
        consultation={selectedConsultation}
      />

      <ScheduleEventTypePickerModal
        open={eventTypePickerOpen}
        onClose={() => setEventTypePickerOpen(false)}
        onScheduleJob={handleEventTypePickerScheduleJob}
        onAddTask={handleEventTypePickerAddTask}
      />

      <JobPickerModal
        open={jobPickerOpen}
        onClose={() => setJobPickerOpen(false)}
        onJobSelected={handleMonthJobSelected}
      />
      <CreateEventFromJobModal
        open={createEventOpen}
        onClose={() => {
          setCreateEventOpen(false)
          setCreateEventJob(null)
        }}
        job={createEventJob}
        selectedDate={createEventDay ?? undefined}
      />

      <TaskEventModal
        open={taskModalOpen}
        onClose={() => {
          setTaskModalOpen(false)
          setSelectedTask(null)
          setTaskModalDay(null)
        }}
        event={selectedTask}
        selectedDate={taskModalDay ?? undefined}
      />
    </div>
  )
})

export default ScheduleMonthView
